/**
 * WarrantBreakEvenPill — Round P141·6 (Feb 2026)
 *
 * Shared clickable "±X% to BE / X% ITM" pill used in three places:
 *   1. Warrant Screener table (To Break-Even column)
 *   2. Stock Modal Warrant Panel (Insights tab)
 *   3. Stock SEO page (/stock/<WARRANT>)
 *
 * Click opens a dialog with underlying + break-even + exercise + a
 * color-coded ITM / at-the-money / OTM explanation. Same modal in
 * all three surfaces so mobile users can read the details the old
 * hover `title` tooltip only exposed on desktop.
 *
 * Props:
 *   - warrant: the warrant doc with `heat_strip`, `exercise_price`,
 *              `underlying_ticker`, `ticker`.
 *   - size: 'sm' (default, table pill) or 'md' (bigger card variant).
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

export default function WarrantBreakEvenPill({ warrant, size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const dp = warrant?.heat_strip?.distance_pct;
  if (dp == null) {
    // No pricing yet — render a muted dash so callers can drop the
    // component in unconditionally without branching.
    return <span className="text-muted-foreground/60 text-xs">—</span>;
  }
  const inMoney = dp <= 0;
  const atMoney = Math.abs(dp) < 5;
  const bg = inMoney
    ? (Math.abs(dp) > 20
        ? 'bg-emerald-500/25 text-emerald-800 dark:text-emerald-300 ring-emerald-500/50'
        : 'bg-emerald-400/20 text-emerald-800 dark:text-emerald-300 ring-emerald-400/40')
    : atMoney
    ? 'bg-amber-500/25 text-amber-800 dark:text-amber-300 ring-amber-500/50'
    : (dp > 100
        ? 'bg-rose-600/30 text-rose-800 dark:text-rose-300 ring-rose-600/60'
        : 'bg-rose-500/20 text-rose-700 dark:text-rose-300 ring-rose-500/40');
  const label = inMoney
    ? `${Math.abs(dp).toFixed(1)}% ITM`
    : `+${dp.toFixed(1)}% to BE`;

  const pillCls = size === 'md'
    ? 'inline-flex items-center px-2 py-1 rounded text-xs font-mono font-bold ring-1 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-violet-400'
    : 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ring-1 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-violet-400';

  return (
    <>
      <button
        type="button"
        className={`${pillCls} ${bg}`}
        onClick={() => setOpen(true)}
        data-testid={`warrant-be-pill-${warrant.ticker}`}
        aria-label={`Break-even details for ${warrant.ticker}`}
      >
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="warrant-be-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">{warrant.ticker} · Break-Even Details</DialogTitle>
            <DialogDescription>
              How this warrant&rsquo;s underlying stock stacks up against the price it needs to hit for you to break even at exercise.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Underlying</div>
                <div className="font-mono text-base">{warrant.underlying_ticker || '—'}</div>
                <div className="font-mono text-xs text-muted-foreground">
                  {warrant.heat_strip?.underlying_price != null
                    ? `$${warrant.heat_strip.underlying_price.toFixed(2)}`
                    : 'price n/a'}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Break-Even</div>
                <div className="font-mono text-base">
                  {warrant.heat_strip?.break_even_underlying != null
                    ? `$${warrant.heat_strip.break_even_underlying.toFixed(2)}`
                    : '—'}
                </div>
                <div className="font-mono text-xs text-muted-foreground">exercise + warrant premium</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Exercise Price</div>
                <div className="font-mono text-base">
                  {warrant.exercise_price != null ? `$${warrant.exercise_price.toFixed(2)}` : '—'}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Current Position</div>
                <div className={`font-mono text-base ${inMoney ? 'text-emerald-600 dark:text-emerald-400' : atMoney ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {label}
                </div>
              </div>
            </div>
            <div className="rounded-md bg-muted/40 p-3 text-xs leading-relaxed">
              {inMoney ? (
                <>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">In-the-money.</span>{' '}
                  The underlying is trading above the break-even price, so exercising the warrant today would net you a profit
                  (before commissions and any warrant premium already paid).
                </>
              ) : atMoney ? (
                <>
                  <span className="font-semibold text-amber-700 dark:text-amber-400">At-the-money zone.</span>{' '}
                  The underlying is within 5% of the break-even price. Any small underlying move flips your intrinsic
                  value, so watch this warrant closely — especially for 8-K redemption filings.
                </>
              ) : (
                <>
                  <span className="font-semibold text-rose-700 dark:text-rose-400">Out-of-the-money.</span>{' '}
                  The underlying needs to rise <span className="font-mono">+{dp.toFixed(1)}%</span> to reach
                  the break-even price. Warrants CAN expire worthless — check the expiry date on the linked SEC 8-K.
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
